import React, { useEffect, useState, useRef } from 'react';
import { FormProvider, useForm, Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAppDispatch, RootState } from '@/store';
import { getBookingList, getBookingSummary } from '@/store/bookings/action';
import { getUserVenues } from '@/store/user-info/action';
import useCustomPaging from '@/hooks/useCustomPaging';
import useSearchFilter from '@/hooks/useSearchFilter';
import useUserVenueSelectFilter from '@/hooks/useUserVenueSelectFilter';
import useGenericSelectFilter from '@/hooks/useGenericSelectFilter';
import useMultiDatePickerFilter from '@/hooks/useMultiDatePickerFilter';
import { dynamicAttr } from '@/utils/fallback';
import { constructTableMetaFromPaging2 } from '@/utils/paging';
import { currencyFormat } from '@/utils'
import {
  renderTableStatus,
  renderTableEditButton, renderTableBookingType, renderTableCustomer, renderTableCreatedDate, renderTableBookingId,
} from '@/pages/Bookings/List/Table';
import { ReactComponent as AddIcon } from '@/assets/icons/ri-add-line.svg'
import BreadcrumbLayout from '@/components/common/CustomLayout/BreadcrumbLayout'
import Table from '@/components/common/Table'
import Button from '@/components/common/Button'
import GenericMultiDatePicker from '@/components/common/GenericMultiDatepicker'
import { BOOKING_TYPE_FILTER_COLOR_MAPPING, BOOKING_TYPE, statusOptions } from '@/pages/Bookings/constant';
import UserVenueSelect from '@/components/common/UserVenueSelect';
import GenericSelect from "@/components/common/GenericSelect";

const statusSelectOptions = statusOptions.map(s => ({
  value: s,
  label: s
}))

const renderBookingHeader = (
  searchString: string,
  handleChangeSearchString: any,
  handleSubmitSearch: any,
  searchBooking: Function,
  selectFilter: Function,
  navigate: Function,
  venueOptions,
  selectedVenue,
  handleChangeVenue) => (
  <BreadcrumbLayout>
    <div className="flex justify-between items-center gap-8 p-0">
      <div className="flex items-center p-0 gap-8">
        <p className="font-bold text-2xl text-black-primary">Bookings</p>
        <div className='bg-grey-line-subtle h-6 w-1'/>
        <UserVenueSelect
            options={venueOptions}
            value={selectedVenue}
            placeholder='Select venue'
            onChange={handleChangeVenue}
        />
      </div>
      <div className="flex items-center gap-4">
        <input
          value={searchString}
          onChange={handleChangeSearchString}
          onKeyDown={handleSubmitSearch}
          name="searchString"
          type="text"
          className="flex items-center py-[8px] px-4 gap-2 border rounded border-solid border-stroke-subtle text-jsm font-normal"
          placeholder="Search for Booking"
        />
        <div className="bg-grey-line-subtle h-6 w-1"/>
        <Button type="submit" variant="primary" className="text-white" onClick={() => navigate('/dashboard/booking/add')}>
          <AddIcon/>
          <p>Booking</p>
        </Button>
      </div>
    </div>
  </BreadcrumbLayout>
)

const renderFilterSection = (
  { filterMethods }: any,
  selectFilter: Function,
  filterMeta: BookingFilter,
  selectedStatus,
  selectStatusOptions,
  startDate,
  endDate,
  bookingSummary: any,
  inputRef: any,
  setDate: Function,
  setSelectedStatus: Function) => (
  <div
    className='flex justify-between items-center p-4 p-6 bg-white border border-grey-line-subtle border-b-0 rounded-t'>
    <div className='flex items-center p-0 gap-4'>
      <div className='flex items-start'>
        {bookingSummary && BOOKING_TYPE(bookingSummary).map(type => {
          const btnClass = type.key === filterMeta.type ? BOOKING_TYPE_FILTER_COLOR_MAPPING.ACTIVE : BOOKING_TYPE_FILTER_COLOR_MAPPING.INACTIVE;
          return (
            <Button type='button'
                    variant='primary'
                    key={type.key}
                    className={
                      `border
                    whitespace-nowrap
                     border-grey-line-subtle
                     rounded-none 
                     gap-3
                     ${btnClass.button}`}
                    onClick={() => selectFilter({type: type.key})}>
              {type.label}
              <div
                className={`flex py-0 px-2 gap-3 rounded-full ${btnClass.count}`}>
                {type.count}
              </div>
            </Button>);
        })}
      </div>
      <div className='bg-grey-line-subtle h-6 w-1' />
      <FormProvider {...filterMethods}>
        <Controller
          name='status'
          render={({ field }) =>
            <GenericSelect
              styles={{
                control: style => ({ ...style, width: '12em' }),
              }}
              options={selectStatusOptions}
              value={selectedStatus}
              placeholder='Select status'
              {...filterMethods.register('status')}
              {...field}
              onChange={setSelectedStatus}
            />
          }
        />
      </FormProvider>
      <GenericMultiDatePicker
        startDate={startDate}
        endDate={endDate}
        onChange={(dates) => {
          setDate(dates)
        }}
      />
    </div>
    <div className='flex items-center p-0 gap-2'>
      {/* TODO: To dropdown and do actual pagination */}
      10 Item Per Page
    </div>
  </div>
)

const HEADERS: TableHeader[] = [
  {
    label: 'Booking ID',
    key: 'customId',
  },
  {
    label: 'Customer',
    key: 'customerName',
  },
  {
    label: 'Booking Type',
    key: 'bookingType',
  },
  {
    label: 'total',
    key: 'finalPrice'
  },
  {
    label: 'status',
    key: 'status'
  },
  {
    label: 'Created Date',
    key: 'createdAt'
  },
  {
    label: 'Action',
    key: 'action',
  }
]
const SORT: TableSort = {
  type: 'SERVER',
  handler: () => {}
}

const constructTableData = (data, navigate) => {
  const tableBody = data.map(booking => {
    const {
      id,
      custom_id: customId,
      customer: customerName,
      type: bookingType,
      final_price: finalPrice,
      status,
      created_at: createdAt,
      invoice_id: invoiceId,
      booking_method_id: bookingMethodId,
      rescheduled_to: rescheduledTo
    } = booking
    return {
      id,
      value:
        {
          customId: {
            customId,
            id,
            bookingMethodId,
            rescheduleToId: rescheduledTo != null ? rescheduledTo.to_custom_id : null
          },
          customerName,
          bookingType,
          finalPrice,
          status,
          createdAt,
          action: {
            id,
            invoiceId
          }
        }
    }
  })
  const component = {
    customId: (v: {customId: string, id: string, bookingMethodId: string, rescheduleToId: string}) => renderTableBookingId(v, navigate),
    customerName: (v) => renderTableCustomer(v),
    bookingType: (v: string) => renderTableBookingType(v),
    finalPrice: (v: number) => (<p className="font-medium text-sm">{currencyFormat(v)}</p>),
    status: (v: string) => renderTableStatus(v),
    createdAt: (v: number) => renderTableCreatedDate(v),
    action: (v: { id: string, invoiceId: string }) => renderTableEditButton(v, navigate),
  }
  return {
    data: tableBody,
    component
  }
}

const Bookings = (): JSX.Element => {
  const [bookingSummary, setBookingSummary] = useState({})
  const dispatch = useAppDispatch()
  const bookingState = useSelector((state: RootState) => state.booking.state.items)
  const paging = useSelector((state: RootState) => state.booking.state.paging)
  const isLoading = useSelector((state: RootState) => state.booking.loading)
  const [searchParams, setSearchParams] = useSearchParams()
  const [currentPageFilter,,handlePageNumberChange] = useCustomPaging()
  const [
    currentVenueIdFilter,
    selectedVenue,
    venueOptions,
    handleChangeVenue,
  ] = useUserVenueSelectFilter()
  const [
    searchString,
    currentSearchFilter,
    handleChangeSearchString,
    handleSubmitSearch
  ] = useSearchFilter()
  const [
    currentStatusFilter,
    selectedStatus,
    selectStatusOptions,
    handleChangeStatus,
  ] = useGenericSelectFilter('status', statusSelectOptions)
  const [
    startDateTimestamp,
    endDateTimestamp,
    currentStartDate,
    currentEndDate,
    handleChangeDate
  ] = useMultiDatePickerFilter(['startDate', 'endDate'])

  const setSortingParam = (key, order) => {
    if (key) {
      return order === 'ASC' ? key : `-${key}`
    }
    return ''
  }

  const getAllBookings = () => {
    const params = {
      sizeMeta: 10,
      ...dynamicAttr('pageMeta', currentPageFilter),
      ...dynamicAttr('venueMeta', currentVenueIdFilter),
      ...dynamicAttr('searchMeta', currentSearchFilter),
      ...dynamicAttr('startDateMeta', startDateTimestamp),
      ...dynamicAttr('endDateMeta', endDateTimestamp),
      ...dynamicAttr('typeMeta', searchParams.get('type') === 'ALL' ? '' : searchParams.get('type')),
      ...dynamicAttr('statusMeta', currentStatusFilter),
      ...dynamicAttr('sortMeta', setSortingParam(searchParams.get('sortBy'), searchParams.get('order'))),
    }
    dispatch(getBookingList(params))
  }

  const inputRef = useRef()
  const navigate = useNavigate()

  const filterMethods = useForm()

  const bookingMeta: BookingFilter = {
    type: searchParams.get('type'),
    status: searchParams.get('status'),
    venue: searchParams.get('venue'),
    search: searchParams.get('search'),
    startDate: Number(searchParams.get('startDate')),
    endDate: Number(searchParams.get('endDate'))
  }
  const handleBookingMeta = ({ type, status, venue, search, startDate, endDate }: BookingFilter) => {
    if (type) searchParams.set('type', type)
    if (status) searchParams.set('status', status)
    if (venue || venue === '') searchParams.set('venue', venue)
    if (search || search === '') searchParams.set('search', search)
    if (startDate || startDate === '') searchParams.set('startDate', startDate.toString())
    if (endDate || endDate === '') searchParams.set('endDate', endDate.toString())
    setSearchParams(searchParams)
  }

  const getBookingTypeSummary = async () => {
    const params = {
      ...dynamicAttr('venueId', currentVenueIdFilter),
      ...dynamicAttr('search', currentSearchFilter),
      ...dynamicAttr('startDate', startDateTimestamp),
      ...dynamicAttr('endDate', endDateTimestamp),
      ...dynamicAttr('status', currentStatusFilter)
    }
    const summary = await dispatch(getBookingSummary(params));
    setBookingSummary(summary?.payload ?? bookingSummary)
  }

  useEffect(() => {
    dispatch(getUserVenues({}))
  }, [])

  useEffect(() => {
    (async () => {
        getAllBookings();
        await getBookingTypeSummary()
      }
  )()
  }, [
    bookingMeta.type,
    currentStatusFilter,
    currentPageFilter,
    currentVenueIdFilter,
    currentSearchFilter,
    startDateTimestamp,
    endDateTimestamp,
    searchParams.get('sortBy'),
    searchParams.get('order')
  ])

  return (
    <div className='flex flex-col w-full'>
      {
        renderBookingHeader(
          searchString,
          handleChangeSearchString,
          handleSubmitSearch,
          handleBookingMeta,
          handleBookingMeta,
          navigate,
          venueOptions,
          selectedVenue,
          handleChangeVenue
        )
      }
      <div className='flex flex-col p-4 w-full'>
        {
          renderFilterSection(
            { filterMethods },
            handleBookingMeta,
            bookingMeta,
            selectedStatus,
            selectStatusOptions,
            currentStartDate,
            currentEndDate,
            bookingSummary,
            inputRef,
            handleChangeDate,
            handleChangeStatus
          )
        }
        {!isLoading && <Table headers={HEADERS}
															body={constructTableData(bookingState, navigate)}
															sort={SORT}
															meta={constructTableMetaFromPaging2(paging)}
                              pageNumberHandler={handlePageNumberChange}/>}
      </div>
    </div>
  )
}

export default Bookings
